import { Link } from './nav'
import { ExternalLink } from 'lucide-react'
import type { Project } from '../data/projectTypes'
import Spinner from './Spinner'
import SharedBadge from './SharedBadge'
import { isSharedProject } from '../lib/projectSharing'
import './ProjectList.css'
import { usePrefetchProjectData } from '../services/projectDataApi'

interface ProjectListProps {
  projects: Project[]
  // Ids of projects that currently have work running — each gets a loading spinner in the card's
  // top-right corner, mirroring the sidebar's per-project spinner.
  activeProjectIds?: Set<string>
}

// Fallback shown until a project has captured any app screenshots.
const PLACEHOLDER_THUMBNAIL = '/images/project-placeholder.svg'

// The project thumbnail is a real screenshot of the tested app — the newest frame captured during
// the project's most recent QA run (`app_screenshot_url`). We deliberately do NOT use the OG card
// here: that image is a QA-results summary card, not a screenshot of the app. When no screenshot has
// been captured yet we fall back to a neutral placeholder.
function thumbnailSrc(project: Project): string {
  return project.app_screenshot_url || PLACEHOLDER_THUMBNAIL
}

// How wide the thumbnail slot renders, transcribed from the layout constants in Projects.css /
// AppShell.css (232px sidebar, 36px list padding, minmax(340px, 1fr) auto-fill grid): the
// single-column bands are 100vw - 26px (mobile, <=768px) and 100vw - 306px (desktop with sidebar,
// 769-999px), the 720-768px band is two columns of (100vw - 46px) / 2, and wider viewports land
// in the 338-518px range. A few percent of accuracy here is what lets the browser choose between
// the candidates below.
const THUMBNAIL_SIZES =
  '(max-width: 768px) and (min-width: 720px) calc((100vw - 46px) / 2), (max-width: 768px) calc(100vw - 26px), (max-width: 999px) calc(100vw - 306px), 480px'

// Every QA run stores the newest frame in two resolutions (docs/backend/task-thumbnails.md): the
// <=480px downscale rendered as src above, plus a high-res companion. Declare both as
// width-descriptor candidates so the browser downloads the small preview at DPR 1 and only
// reaches for the full-size capture when the rendered slot actually needs those pixels — without
// this the grid always fetched the single full-size asset (QA bug bug-mu5zrpgz-mcd0). Frames
// captured before the pair existed have one candidate and keep the plain-src behavior, as does
// the placeholder.
function thumbnailResponsiveProps(project: Project): { srcSet?: string; sizes?: string } {
  const candidates = [
    project.app_screenshot_url && project.app_screenshot_width
      ? `${project.app_screenshot_url} ${project.app_screenshot_width}w`
      : null,
    project.app_screenshot_full_url && project.app_screenshot_full_width
      ? `${project.app_screenshot_full_url} ${project.app_screenshot_full_width}w`
      : null,
  ].filter((candidate): candidate is string => candidate !== null)
  // A single candidate leaves the browser nothing to downselect from; srcset would just restate src.
  if (candidates.length < 2) return {}
  return { srcSet: candidates.join(', '), sizes: THUMBNAIL_SIZES }
}

export default function ProjectList({ projects, activeProjectIds }: ProjectListProps) {
  const prefetchProject = usePrefetchProjectData()

  if (projects.length === 0) {
    return (
      <div className="project-list__empty" role="status" data-testid="project-list-empty">
        {/* Decorative washed-out sketch of a stack of project cards, drawn in the soft-gray
            "empty state illustration" style: a pale blob behind, light gray line work, and
            little plus/dot sparkles. */}
        <svg
          className="project-list__empty-illustration"
          width="200"
          height="172"
          viewBox="0 0 200 172"
          fill="none"
          aria-hidden="true"
        >
          <path d="M124 34c-12 0-24-.5-35.4-3.7-11.2-3.1-21.5-9.1-30.6-16-6-4.5-11.4-8.1-19.2-7.6-7.6.4-14.9 3.3-20.7 8.1C8.2 23.5 9.7 39.5 13.6 50.7c5.9 17 24 28.7 39.4 36.4 17.7 8.9 37.2 14 56.7 17 17.1 2.6 39.1 4.5 54-6.7 13.6-10.3 17.3-33.7 14-49.6-.8-4.7-3.3-8.9-7-11.9-9.6-7-23.9-2.3-34.6-2.1-4 .1-8.1.2-12.1.2z" fill="#F2F2F2" />
          <ellipse cx="100" cy="153" rx="52" ry="4" fill="#F2F2F2" />
          <circle cx="164" cy="27" r="1.4" fill="#CFCFCF" />
          <circle cx="42" cy="18" r="1.4" fill="#CFCFCF" />
          <circle cx="58" cy="140" r="1.4" fill="#CFCFCF" />
          <path d="M139 12v6M136 15h6" stroke="#BABABA" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M23 51v6M20 54h6" stroke="#BABABA" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M31 118v6M28 121h6" stroke="#BABABA" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M172 74v6M169 77h6" stroke="#BABABA" strokeLinecap="round" strokeLinejoin="round" />
          <rect x="52" y="32" width="88" height="88" rx="8" fill="#D2D2D2" transform="rotate(-7 52 32)" />
          <rect x="62" y="40" width="88" height="90" rx="8" fill="white" stroke="#BABABA" strokeLinecap="round" strokeLinejoin="round" />
          <rect x="62.5" y="40.5" width="87" height="42" rx="7.5" fill="#F2F2F2" stroke="#BABABA" />
          <rect x="62.5" y="66.5" width="87" height="16" fill="#F2F2F2" stroke="#BABABA" />
          <rect x="72" y="94" width="48" height="6" rx="3" fill="#DDDDDD" />
          <rect x="72" y="107" width="32" height="6" rx="3" fill="#E8E8E8" />
          <circle cx="142" cy="122" r="16" fill="white" stroke="#BABABA" />
          <path d="M142 115v14M135 122h14" stroke="#BABABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <p>No projects yet. Create one to get started.</p>
      </div>
    )
  }

  return (
    <div className="project-list" data-testid="project-list">
      {projects.map(project => (
        <article
          key={project.id}
          className="project-card"
          data-testid={`project-card-${project.id}`}
        >
          {activeProjectIds?.has(project.id) && (
            <span
              className="project-card__spinner"
              data-testid={`project-card-spinner-${project.id}`}
            >
              <Spinner size={16} strokeWidth={2} label="Work in progress" />
            </span>
          )}
          <div className="project-card__thumbnail" data-testid="project-card-thumbnail">
            <img
              {...thumbnailResponsiveProps(project)}
              src={thumbnailSrc(project)}
              alt=""
              loading="lazy"
              className="project-card__thumbnail-img"
              data-has-thumbnail={project.app_screenshot_url ? 'true' : 'false'}
              onError={(e) => {
                const img = e.currentTarget
                if (img.src.endsWith(PLACEHOLDER_THUMBNAIL)) return
                img.src = PLACEHOLDER_THUMBNAIL
                // Drop the failed source's candidates too — they'd keep overriding the placeholder src.
                img.removeAttribute('srcset')
                img.removeAttribute('sizes')
                img.dataset.hasThumbnail = 'false'
              }}
            />
          </div>
          <div className="project-card__header">
            <h2 className="project-card__name">
              <Link
                to={`/projects/${project.id}/overview`}
                onPointerEnter={() => prefetchProject(project.id)}
                onFocus={() => prefetchProject(project.id)}
                data-testid="project-card-name"
                data-analytics-event="projects-card-open"
              >
                {project.name}
              </Link>
            </h2>
            {isSharedProject(project) && <SharedBadge />}
          </div>
          {/* GitHub-synced projects have no target_url — show the repo URL instead, and nothing when
              there's neither, rather than an empty link. */}
          {(project.target_url || project.github?.repoUrl) && (
            <a
              className="project-card__url"
              data-testid="project-card-url"
              data-analytics-event="projects-card-target-url"
              href={project.target_url || project.github?.repoUrl || undefined}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink size={12} aria-hidden="true" />
              <span>{project.target_url || project.github?.repoUrl}</span>
              <span className="sr-only">(opens in new tab)</span>
            </a>
          )}
        </article>
      ))}
    </div>
  )
}
